import { Form } from 'radix-ui'
import {
  AlertDialog,
  Button,
  Container,
  Flex,
  Heading,
  IconButton,
  ScrollArea,
  Section,
  Table,
  Text,
} from '@radix-ui/themes'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'
import {
  ClaimCenter,
  ResponseError,
  ScheduleExceptionTemplate,
} from '@Generated/back-office-client/src'
import { addYears, lastDayOfYear, parseISO } from 'date-fns'
import { formatDateToDayString } from '@Components/util/utils'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons'

export const ClaimsAdmin = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [deletingDate, setDeletingDate] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const today = useMemo(() => new Date().toISOString(), [])
  const lastDay = useMemo(() => lastDayOfYear(addYears(today, 1)).toISOString(), [today])

  const { data: claimCenters } = useQuery({
    queryKey: [`${activeCustomerId}-claim-centers`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return await ClaimReservationsServiceAPI.getClaimCenters()
    },
  })

  const { data: claimCenterExceptions, refetch: refetchClaimCenterExceptions } = useQuery({
    queryKey: [`${activeCustomerId}-claim-center-exceptions-${today}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    enabled: !!claimCenters,
    queryFn: async () => {
      return await ClaimReservationsServiceAPI.getClaimCenterExceptionsForDateRange(today, lastDay)
    },
  })

  const { mutateAsync: cancelReservation } = useMutation({
    mutationFn: async (reservationId: string) => {
      try {
        return await ClaimReservationsServiceAPI.cancelReservation(reservationId)
      } catch (e) {
        // If we tried to delete a reservation in the past, that's okay, just ignore it
        if (e instanceof ResponseError && e.response.status !== 422) {
          throw e
        }
      }
    },
  })

  const claimCentersById = useMemo(() => {
    return claimCenters?.reduce(
      (acc, cc) => {
        acc[cc.id] = cc
        return acc
      },
      {} as Record<string, ClaimCenter>,
    )
  }, [claimCenters])

  // Group all exceptions by the dates they're on, and the claim centers they're associated with
  // {'2025-11-01': {claimCenterId: [exceptionId, exceptionId]}}
  const claimCenterExceptionsByDate = useMemo(() => {
    return claimCenterExceptions?.reduce(
      (acc, cce) => {
        if (acc[cce.localDate] && acc[cce.localDate][cce.claimCenterId]) {
          acc[cce.localDate][cce.claimCenterId].push(cce.id)
        } else if (acc[cce.localDate]) {
          acc[cce.localDate][cce.claimCenterId] = [cce.id]
        } else {
          acc[cce.localDate] = { [cce.claimCenterId]: [cce.id] }
        }
        return acc
      },
      {} as Record<string, Record<string, string[]>>,
    )
  }, [claimCenterExceptions])

  const {
    mutateAsync: cancelExceptions,
    error: cancelExceptionsError,
    isPending: cancellingExceptions,
    reset: resetDeletionState,
  } = useMutation({
    mutationFn: async (date: string) => {
      const exceptionsToCancel = claimCenterExceptionsByDate![date]
      for (const claimCenterId of Object.keys(exceptionsToCancel)) {
        for (const exceptionId of exceptionsToCancel[claimCenterId]) {
          await ClaimReservationsServiceAPI.cancelException(claimCenterId, exceptionId)
        }
      }

      setDeletingDate(null)
      resetDeletionState()
      refetchClaimCenterExceptions()
    },
  })

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      for (const claimCenter of claimCenters!) {
        const reservations = await ClaimReservationsServiceAPI.getReservations(
          claimCenter.id,
          selectedDate!,
          selectedDate!,
        )

        for (const reservationToCancel of reservations) {
          await cancelReservation(reservationToCancel.id)
        }

        const exception: ScheduleExceptionTemplate = {
          localDate: selectedDate!,
          localStartTime: '00:00:00',
          localEndTime: '23:59:59',
          reason: 'Full day exception for all claim centers',
        }

        await ClaimReservationsServiceAPI.createClaimCenterException(claimCenter.id, exception)
      }
    } catch (e) {
      console.error(e)
      if (e instanceof ResponseError) {
        setError(e.message)
      } else {
        setError('Unknown error')
      }
    } finally {
      setLoading(false)
    }

    await refetchClaimCenterExceptions()
  }

  const getExceptionRows = useCallback(() => {
    if (!claimCenterExceptionsByDate || !claimCentersById) {
      return null
    }

    return Object.keys(claimCenterExceptionsByDate)
      .filter((exceptionDate) => {
        // Only show exceptions that exist across all claim centers
        const claimCentersWithExceptions = Object.keys(claimCenterExceptionsByDate[exceptionDate])
        return claimCentersWithExceptions.length === claimCenters?.length
      })
      .sort((a, b) => parseISO(a).getTime() - parseISO(b).getTime())
      .map((exceptionDate) => {
        return (
          <Table.Row key={exceptionDate}>
            <Table.Cell>All</Table.Cell>
            <Table.Cell>{formatDateToDayString(parseISO(exceptionDate), true)}</Table.Cell>
            <Table.Cell>
              <AlertDialog.Root
                open={deletingDate === exceptionDate}
                onOpenChange={(open) => {
                  if (!open) {
                    setDeletingDate(null)
                    resetDeletionState()
                  }
                }}>
                <AlertDialog.Trigger onClick={() => setDeletingDate(exceptionDate)}>
                  <IconButton variant="soft">
                    <TrashIcon />
                  </IconButton>
                </AlertDialog.Trigger>
                <AlertDialog.Content maxWidth="500px" style={{ paddingBottom: '60px' }}>
                  <AlertDialog.Cancel>
                    <Flex justify="end">
                      <IconButton variant="ghost" aria-label="Cancel">
                        <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
                      </IconButton>
                    </Flex>
                  </AlertDialog.Cancel>
                  <AlertDialog.Title align="center" style={{ paddingTop: '29px' }}>
                    Are you sure?
                  </AlertDialog.Title>
                  <AlertDialog.Description
                    size="2"
                    style={{ paddingBottom: '18px' }}
                    align="center">
                    This action cannot be reversed.
                  </AlertDialog.Description>
                  <Flex gap="3" justify="center">
                    <Button
                      color="red"
                      onClick={() => {
                        cancelExceptions(exceptionDate)
                      }}
                      loading={cancellingExceptions}>
                      Delete
                    </Button>
                  </Flex>
                  {cancelExceptionsError ? (
                    <Flex justify="center">
                      <Text color="red">
                        There was an error deleting unavailability: {cancelExceptionsError.message}
                      </Text>
                    </Flex>
                  ) : null}
                </AlertDialog.Content>
              </AlertDialog.Root>
            </Table.Cell>
          </Table.Row>
        )
      })
  }, [
    cancelExceptions,
    cancelExceptionsError,
    cancellingExceptions,
    claimCenterExceptionsByDate,
    claimCenters?.length,
    claimCentersById,
    deletingDate,
    resetDeletionState,
  ])

  if (!claimCenters) {
    return null
  }

  return (
    <>
      <Container>
        <Section>
          <Flex direction="column" align="center" gap="2">
            <Heading>Schedule Global Unavailability</Heading>
            <Text>This will block out time at all claim centers</Text>
            <Form.Root onSubmit={onSubmit}>
              <Flex direction="column" gap="3" width="300px">
                <Form.Field name="date">
                  <Form.Label>Select Date</Form.Label>
                  <Form.Control asChild>
                    <input
                      type="date"
                      className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </Form.Control>
                </Form.Field>

                <Form.Submit asChild>
                  <Button disabled={!selectedDate || loading} loading={loading && !!selectedDate}>
                    Submit
                  </Button>
                </Form.Submit>
                {error ? <Text color="red">Error scheduling unavailability {error}</Text> : null}
              </Flex>
            </Form.Root>
          </Flex>
        </Section>
      </Container>
      <Flex direction="column" align="center" gap="2">
        <Heading>Currently Scheduled Exceptions</Heading>
        <Text>All full day exceptions scheduled for every claim center, this year and next.</Text>
        <Flex className="global-exceptions-container">
          <ScrollArea radius="none" type="scroll" scrollbars="vertical">
            <Table.Root size="3" className="global-exceptions-table">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Claim Center(s)</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Delete</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>{getExceptionRows()}</Table.Body>
            </Table.Root>
          </ScrollArea>
        </Flex>
      </Flex>
    </>
  )
}
